import {Button, FileUpload, Icon, Loader, Modal} from '@/components/atoms'
import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {uploadImagesService} from '@/services/uploadImages'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants, FILE} from '@/utils/constants/commonConstants'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useCallback, useState} from 'react'

function UploadMedia({
  setMediaCardFormData,
  mediaCardFormData,
  // uploadFor = null,
  setPublishDisabled,
  mediaText = 'Add Media',
  productId,
}) {
  const initialFileErrorState = {feedbackType: 'info', helperText: ''}
  const [open, setOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [images, setImages] = useState([])
  const [imageUri, setImageUri] = useState('')
  const [fileError, setFileError] = useState(initialFileErrorState)
  const [imgError, setImgError] = useState(false)
  const [metadata, setMetadata] = useState([])
  const [progress, setProgress] = useState(0)
  const [convertedImg, setConvertedImg] = useState({})
  const dispatch = useAppDispatch()
  const {maxIndividualImageSize = '2MB'} = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  const isCloseModal = () => {
    setImages([])
    setImageUri('')
    setOpen(false)
    setConvertedImg([])
    setMetadata([])
    setUploading(false)
    setFileError(initialFileErrorState)
  }

  const isOpenModal = useCallback(() => {
    setOpen(true)
  }, [])

  const handleDelete = useCallback(() => {
    setPublishDisabled(true)
    setMediaCardFormData({media: {mediaUrl: '', title: ''}})
    setImages([])
    setImageUri('')
    setImgError(false)
    setConvertedImg([])
    setMetadata([])
  }, [setMediaCardFormData, setPublishDisabled])

  const onUploadProgress = progressEvent => {
    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    setProgress(progress)
  }

  const handleDone = () => {
    if (!convertedImg || metadata.length === 0) {
      setFileError({
        feedbackType: 'error',
        helperText: 'Image or metadata is missing. Please try again.',
      })
      return
    }

    setUploading(true)

    const formData = new FormData()
    formData.append('images', convertedImg)
    formData.append('metadata', JSON.stringify({mediaUploadMetadata: metadata}))

    uploadImagesService({formData, onUploadProgress})
      .then(response => {
        if (response?.status === 'SUCCESS') {
          isCloseModal()
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Image uploaded successfully',
              type: 'success',
            })
          )
          setPublishDisabled(false)
          const responseData = response.data?.[0]
          // Add a random query parameter to force browser to fetch new image
          const imageUrl = responseData?.imageCDNUrl
          setMediaCardFormData({media: {...mediaCardFormData, mediaUrl: imageUrl, title: responseData?.id}})
        } else {
          setFileError({
            feedbackType: 'error',
            helperText: 'Upload failed. Please try again.',
          })
        }
      })
      .catch(error => {
        setFileError({
          feedbackType: 'error',
          helperText: error?.message,
        })
        setImages([])
        setImageUri('')
        setConvertedImg([])
        setMetadata([])
      })
      .finally(() => {
        setUploading(false)
      })
  }

  const handleImageError = useCallback(() => {
    setImgError(true)
  }, [])

  const handleUpload = async file => {
    if (!file) return

    if (file.size > Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES) {
      setFileError({
        feedbackType: 'error',
        helperText: 'Each image must be less than or equal to 2 MB.',
      })
      return
    }

    try {
      setUploading(true)
      setFileError(initialFileErrorState)

      const fileURL = URL.createObjectURL(file)
      setImages([file])
      setImageUri(fileURL)
      const ext = file?.type?.replace('image/', '')
      const renamedFile = new File([file], `${productId}.${ext}`, {
        type: file.type,
        lastModified: file.lastModified,
      })
      const convertedFiles = await convertImageToWebP(renamedFile, {
        entityType: 'product',
      })

      if (convertedFiles?.metadata && convertedFiles?.webpFile) {
        setMetadata([convertedFiles.metadata])
        setConvertedImg(convertedFiles.webpFile)
      }
    } catch (error) {
      setFileError({
        feedbackType: 'error',
        helperText: 'Failed to process file. Supported formats: .jpg, .png',
      })
    } finally {
      setUploading(false)
    }
  }

  const handleUploadError = useCallback(error => {
    setFileError({
      feedbackType: 'error',
      helperText: error || 'Invalid file format. Please use .jpg or .png files only.',
    })
  }, [])

  const hasImage = !!mediaCardFormData?.mediaUrl
  return (
    <>
      {hasImage ? (
        <div className="mb-10 flex flex-col gap-6">
          <p className="text-md font-bold">Product</p>
          <div className="grid grid-cols-3 items-start gap-3">
            <div className="rounded-lg border border-gray-600 p-3">
              <div className="grid grid-cols-2 gap-6">
                <div
                  className="image-container flex items-center justify-center bg-neutral-700 p-2"
                  style={{minHeight: '200px'}}
                >
                  {!imgError ? (
                    <img
                      src={mediaCardFormData.mediaUrl}
                      alt={mediaCardFormData.title || 'Media'}
                      className="max-h-64 max-w-full rounded object-contain"
                      onError={handleImageError}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4 text-center">
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="24" height="24" fill="#999" fillOpacity="0.2" rx="4" />
                        <path d="M8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="#999" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-400">Image preview unavailable</p>
                    </div>
                  )}
                </div>
                <div className="details-container">
                  <div className="mb-4 flex items-start justify-end">
                    <Icon
                      data-testid="delete-icon"
                      icon="delete"
                      size="small"
                      onClick={handleDelete}
                      className="cursor-pointer text-white hover:text-red-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Title</label>
                    <p className="w-full rounded-md bg-neutral-700 px-4 py-2 text-white">
                      {mediaCardFormData.title || ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <h1 className="mb-4 text-sm font-bold">{mediaText}</h1>
          <div className="mb-6 flex flex-wrap py-2" data-testid="uploadMediaBtn">
            <Button
              handleButton={isOpenModal}
              uiSchema={{
                size: 'large',
                title: 'Upload Media',
                variant: 'primary',
                iconLeading: 'add_box',
              }}
            />
          </div>
        </>
      )}
      <Modal
        type={MODAL_TYPE.SMALL}
        isDark={true}
        onCloseModal={isCloseModal}
        onClickSecondaryButton={isCloseModal}
        onClickPrimaryButton={handleDone}
        primaryButtonText="Done"
        showTertiaryButton={false}
        showAuxiliaryButton={false}
        showSecondaryButton={true}
        showLink={false}
        showModal={open}
        title={'Upload Media'}
        primaryButtonDisabled={images.length <= 0}
        content={
          uploading ? (
            <Loader />
          ) : (
            <div className="media-upload p-4">
              <FileUpload
                acceptedExtensions=".jpg,.png,.webp"
                description="Drag and drop your file here (only .jpg,.png,.webp)"
                label="Upload image file"
                variant={uploading ? Constants.Uploading : Constants.Edit}
                buttonLabel="Select from System Library"
                onUpload={handleUpload}
                onUploadError={handleUploadError}
                onUploadDelete={() => {
                  setImages([])
                  setImageUri('')
                }}
                type="image"
                required={true}
                files={images}
                imageUri={imageUri}
                className={images?.length > 0 && fileError.feedbackType == 'error' ? 'disabled' : ''}
                containerClassName={images.length > 0 ? 'disabled' : ''}
                isDark={true}
                feedbackType={fileError.feedbackType}
                helperText={fileError.helperText}
                dataTestId="upload-image"
                progressPercentage={progress}
              />
            </div>
          )
        }
      />
    </>
  )
}

export default UploadMedia
